import { ref, computed } from 'vue'
import { eventReservationsApi, type MoveRequest, type CancellationRequest } from '../api/eventReservationsApi'
import { useProjectsStore } from '@/modules/projects/stores/useProjectsStore'
import { logger } from '@/core/utils/logger'

/**
 * Composable for managing pending requests (move + cancellation)
 * Provides reactive count for notification badge and full request data
 */
export function usePendingRequests() {
  const projectsStore = useProjectsStore()

  const moveRequests = ref<MoveRequest[]>([])
  const cancellationRequests = ref<CancellationRequest[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const moveRequestsCount = computed(() => moveRequests.value.length)
  const cancellationRequestsCount = computed(() => cancellationRequests.value.length)

  const totalPendingCount = computed(() => {
    return moveRequestsCount.value + cancellationRequestsCount.value
  })

  const hasPendingRequests = computed(() => {
    return totalPendingCount.value > 0
  })

  async function fetchPendingCounts() {
    const projectCode = projectsStore.currentProject?.code
    if (!projectCode) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const [moveReqs, cancelReqs] = await Promise.all([
        eventReservationsApi.getPendingMoveRequests(projectCode),
        eventReservationsApi.getPendingCancellationRequests(projectCode)
      ])

      moveRequests.value = moveReqs
      cancellationRequests.value = cancelReqs
    } catch (err: unknown) {
      logger.error('Failed to fetch pending requests count', err)
      error.value = 'Nepodařilo se načíst počet čekajících žádostí'
    } finally {
      isLoading.value = false
    }
  }

  // Auto-refresh every 30 seconds
  let refreshInterval: ReturnType<typeof setInterval> | null = null

  function startAutoRefresh(intervalMs: number = 30000) {
    stopAutoRefresh()
    refreshInterval = setInterval(() => {
      fetchPendingCounts()
    }, intervalMs)
  }

  function stopAutoRefresh() {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  }

  return {
    moveRequests,
    cancellationRequests,
    moveRequestsCount,
    cancellationRequestsCount,
    totalPendingCount,
    hasPendingRequests,
    isLoading,
    error,
    fetchPendingCounts,
    startAutoRefresh,
    stopAutoRefresh
  }
}
